import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import AuthRoutes from '../../AuthRoutes';
import { useAuthContext } from '../../contexts/AuthContext';
import AdminRoutes from '../../../admin/AdminRoutes';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';
import { useAppTheme } from '../../../../app/features/theme/providers/AppThemeProvider';
import { useOktaAuth } from '@okta/okta-react';
import useAuthOkta from '../../../../app/hooks/useAuthOkta';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import { globalConfig } from '../../../../app/config/config';
import { useOidc } from '@axa-fr/react-oidc';

const AuthLogout = () => {
	const employeeClientId = globalConfig.get().APP_OKTA_CLIENT_ID_AUTH;
	const investorClientId = globalConfig.get().APP_OKTA_USER_CLIENT_ID_AUTH;

	const { logout } = useAuthContext();
	const appTheme = useAppTheme();
	const {
		authContext,
		authOverrideUserId,
		resetAuthOverrideUserId,
		resetCurrentCompany,
		authProvider,
		resetAuthProvider,
	} = useAuthContext();
	const okta = useOktaAuth();
	const oktaEmployee = useAuthOkta();
	const oktaInvestor = useAuthOktaUser();
	const navigate = useNavigate();
	const appNavigationContext = useAppNavigationContext();
	const oidc = useOidc();

	const [isSigninOut, setIsSigninout] = useState(false);

	const logoutWithOkta = useCallback(() => {
		if (okta) {
			let isLocal = false;
			if (['localhost'].includes(window.location.hostname)) {
				isLocal = true;
			}

			// Reset the Okta session
			return okta.oktaAuth
				.isAuthenticated()
				.then(async (isAuthenticated): Promise<boolean> => {
					if (isAuthenticated) {
						if (!okta?.authState?.accessToken) {
							okta.oktaAuth.revokeAccessToken();
							okta.oktaAuth.revokeRefreshToken();
							okta.oktaAuth.storageManager.getTransactionStorage().clearStorage();
							okta.oktaAuth.tokenManager.clear();
							okta.oktaAuth.idx.clearTransactionMeta();
						} else {
							if (investorClientId === okta?.authState?.accessToken?.claims['cid']) {
								await oktaInvestor.signOut({
									revokeAccessToken: true,
									revokeRefreshToken: true,
									clearTokensBeforeRedirect: true,
									postLogoutRedirectUri: location.origin + HAppRoutes.getPath(AuthRoutes.logout.tag),
								});
								return true;
							} else if (employeeClientId === okta?.authState?.accessToken?.claims['cid']) {
								// Employee => Redirect to logout after signOut on okta
								await oktaEmployee.signOut({
									revokeAccessToken: isLocal ? false : true,
									revokeRefreshToken: isLocal ? false : true,
									clearTokensBeforeRedirect: true,
								});
								navigate(HAppRoutes.getPath(AuthRoutes.logout.tag));
								return true;
							}
						}
					}
					return Promise.resolve(false);
				})
				.finally(() => {
					logout();
					navigate(HAppRoutes.getPath(AuthRoutes.login.tag));
				});
		}
	}, [employeeClientId, investorClientId, logout, navigate, okta, oktaEmployee, oktaInvestor]);

	useEffect(() => {
		if (isSigninOut === false) {
			setIsSigninout(true);

			if (authContext === 'employee' && authOverrideUserId !== null) {
				// Logout from an override user
				resetAuthOverrideUserId();
				resetCurrentCompany();

				appNavigationContext.resetCurrentCurrency();
				appNavigationContext.setDisplayShareLink(false);
				navigate(HAppRoutes.getPath(AdminRoutes.investors_list.tag));
			} else if (authProvider === 'azure') {
				resetAuthProvider();
				oidc.logout(HAppRoutes.getPath(AuthRoutes.login.tag));
			} else {
				logoutWithOkta();
				resetAuthProvider();
			}
		}
	}, [
		appNavigationContext,
		appTheme,
		authContext,
		authOverrideUserId,
		authProvider,
		isSigninOut,
		logoutWithOkta,
		navigate,
		oidc,
		okta,
		oktaEmployee,
		oktaInvestor,
		resetAuthOverrideUserId,
		resetAuthProvider,
		resetCurrentCompany,
	]);

	return <></>;
};

export default AuthLogout;
